import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Iconify from 'src/components/Iconify';
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';

import {
    CAMPANHAS_DESIGN as D,
    configStatusCampanha,
    configTipoEnvio,
    configTipoPublico,
} from '../Campanhas.design';
import CampanhaProgressoBar from './CampanhaProgressoBar';

const formatarData = (valor) => {
    if (!valor) return '—';
    return new Date(valor).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

export default function CampanhasTabela({ campanhas, onAcompanhar, onEditar, onExcluir }) {
    const { t } = useTranslation('marketing');

    const headLabels = useMemo(() => [
        { id: 'nome', label: t('campaigns.table.campaign'), className: 'flex-1 min-w-[220px] pr-4' },
        { id: 'progresso', label: t('campaigns.table.progress'), className: 'w-[200px] pr-4' },
        { id: 'status', label: t('campaigns.table.status'), className: 'w-[130px]' },
        { id: 'envio', label: t('campaigns.table.shipping'), className: 'w-[150px]' },
        { id: 'criado', label: t('campaigns.table.created'), className: 'w-[150px]' },
        { id: 'acoes', label: '', className: 'w-[90px]' },
    ], [t]);

    return (
        <GenericTable headLabels={headLabels}>
            {campanhas.map((campanha) => {
                const cfgStatus = configStatusCampanha(campanha.status_envio, t);
                const cfgTipo = configTipoEnvio(campanha.tipo_envio, t);
                const cfgPublico = configTipoPublico(campanha.tipo, t);

                return (
                    <div
                        key={campanha._id}
                        className={`${GENERIC_TABLE_DESIGN.row.container} cursor-pointer`}
                        onClick={() => onAcompanhar(campanha)}
                    >
                        <div className="flex-1 min-w-[220px] pr-4 flex items-center gap-3">
                            <div className={GENERIC_TABLE_DESIGN.common.iconBox}>
                                <Iconify icon={cfgTipo.icon} width={18} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">
                                    {campanha.nome}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className={`${D.destinatario.chip} ${cfgTipo.classe}`}>
                                        {cfgTipo.label}
                                    </span>
                                    {cfgPublico && (
                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                            <Iconify icon={cfgPublico.icon} width={11} />
                                            {cfgPublico.label}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="w-[200px] pr-4">
                            <CampanhaProgressoBar progresso={campanha.progresso} />
                        </div>

                        <div className="w-[130px]">
                            <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md ${cfgStatus.bg} ${cfgStatus.text}`}
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${cfgStatus.dot}`} />
                                {cfgStatus.label}
                            </span>
                        </div>

                        <div className="w-[150px] text-xs text-gray-600 dark:text-[#ABABAD] tabular-nums">
                            {formatarData(campanha.data_envio)}
                        </div>

                        <div className="w-[150px]">
                            <p className="text-xs text-gray-600 dark:text-[#ABABAD] tabular-nums">
                                {formatarData(campanha.data_criacao)}
                            </p>
                            {campanha.criado_por_nome && (
                                <p className="text-[10px] text-gray-400 truncate">{campanha.criado_por_nome}</p>
                            )}
                        </div>

                        <div
                            className="w-[90px] flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                title={t('campaigns.actions.track')}
                                onClick={() => onAcompanhar(campanha)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
                            >
                                <Iconify icon="mdi:chart-timeline-variant" width={16} />
                            </button>
                            <button
                                type="button"
                                title={t('campaigns.actions.edit')}
                                onClick={() => onEditar(campanha)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
                            >
                                <Iconify icon="eva:edit-2-outline" width={16} />
                            </button>
                            <button
                                type="button"
                                title={t('campaigns.actions.delete')}
                                onClick={() => onExcluir(campanha)}
                                className={GENERIC_TABLE_DESIGN.common.deleteButton}
                            >
                                <Iconify icon="eva:trash-2-outline" width={16} />
                            </button>
                        </div>
                    </div>
                );
            })}
        </GenericTable>
    );
}

CampanhasTabela.propTypes = {
    campanhas: PropTypes.array.isRequired,
    onAcompanhar: PropTypes.func.isRequired,
    onEditar: PropTypes.func.isRequired,
    onExcluir: PropTypes.func.isRequired,
};
